<template>
  <div>
    <!-- <student-lookup v-model="student" :required="true" hide-details /> -->
    <v-checkbox
      v-if="schedules && schedules.length > 0"
      v-model="hideCancelled"
      label="Hide canceled schedules"
    />

    <div v-if="student">
      <div class="d-flex flex-column-reverse flex-md-row justify-space-between">
        <div class="my-auto text-subtitle-2">
          {{ student.firstName }} {{ student.lastName }}'s Schedules
        </div>

        <div class="ml-auto mb-2 text-right d-flex">
          <a
            v-if="studentDocumentCount && studentDocumentCount > 0"
            @click="openCompliance"
          >
            {{ studentDocumentCount }} Documents
          </a>
          <v-divider
            class="mx-2"
            v-if="studentDocumentCount && studentDocumentCount > 0"
            vertical
          />
          <a @click="viewAttendances" class="ml-2"> Attendance </a>
          <v-divider class="mx-2" vertical />
          <a @click="newStudentSchedule"> Add New Schedule </a>
        </div>
      </div>

      <v-tabs
        v-model="tab"
        background-color="white"
        color="blue"
        :show-arrows="false"
      >
        <v-tab key="active">Active Schedules</v-tab>
        <v-tab key="past">Past Schedules</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item key="active">
          <div v-if="student">
            <div
              class="text-caption my-4"
              v-if="!schedules || schedules.length < 1"
            >
              No active schedule found for {{ student.firstName }}
            </div>
            <div v-else>
              <student-schedule-timeline
                v-if="loaded"
                v-model="schedules"
                @on-edit="editSchedule"
                @on-undo-cancellation="undoCancellation"
                :getProgramName="getProgramName"
                :getAssignmentInfo="getAssignmentInfo"
                :scheduleExclusionDates="scheduleExclusionDates"
                :hideCancelled="hideCancelled"
                :key="componentKey"
              />

              <v-skeleton-loader
                v-else
                type="list-item, list-item-two-line, list-item, list-item-two-line"
              />
            </div>
          </div>
        </v-tab-item>

        <v-tab-item key="past">
          <div v-if="student">
            <div
              class="text-caption my-4"
              v-if="!schedules || schedules.length < 1"
            >
              No past schedule found for {{ student.firstName }}
            </div>
            <div v-else>
              <student-schedule-timeline
                v-if="loaded"
                v-model="schedules"
                :showPastDates="true"
                @on-edit="editSchedule"
                @on-undo-cancellation="undoCancellation"
                :getProgramName="getProgramName"
                :getAssignmentInfo="getAssignmentInfo"
                :scheduleExclusionDates="scheduleExclusionDates"
                :hideCancelled="hideCancelled"
                :key="componentKey"
              />
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>
    <div v-else class="text-caption">Please choose a student first</div>
  </div>
</template>

<script>
import dialogMixin from "@/components/schedule/dialogMixin.js";
import {
  fetchStudentSchedulesByStudentIds,
  getScheduleAssignments,
  getScheduleExclusionDates,
  undoCancelOccurrence
} from "@/services/studentScheduleService.js";

import { mapGetters } from "vuex";
import moment from "moment";
import {
  openNewStudentSchedule,
  openStudentAttendance,
  openEditStudentScheduleInstance,
} from "@/components/schedule/eventHandler.js";
const StudentScheduleTimeline = () =>
  import("@/components/student/StudentScheduleTimeline.vue");
import { EventBus } from "@/EventBus.js";


export default {
  mixins: [dialogMixin],
  components: { StudentScheduleTimeline },
  props: {
    value: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    loaded: false,
    tab: "active",
    student: null,
    schedules: [],
    programId2Name: {},
    scheduleId2Assignment: {},
    scheduleExclusionDates: [],
    componentKey: 0,
    studentDocumentCount: 0,
    hideCancelled: true,
  }),
  watch: {
    value(newVal) {
      this.student = newVal;
    },
    student(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchSchedule(newVal);
        this.componentKey += 1;

        //Check if there's any compliance documents
        if (this.complianceModule) {
          this.fetchDocumentsCounts();
        }
      }
    },
  },
  computed: {
    ...mapGetters(["globalCenter", "getCenterName", "userModules"]),
    complianceModule() {
      if (this.userModules) {
        return this.userModules.find((m) => m.name == "Compliance");
      } else {
        return null;
      }
    },
  },
  mounted() {
    this.student = this.value;
    this.loadPrograms();
  },
  created() {
    EventBus.$on("reload-dialog", (uuID, instructions) => {
      //The containing dialog is the main component
      if (uuID == this.uuID) {
        const action = instructions.action;
        const scheduleIds = this.schedules.map((s) => s.id);
        // const schedule = instructions.schedule;

        // Define an async function
        const executeAction = async () => {
          if (action === "deleted") {
            this.scheduleExclusionDates = await getScheduleExclusionDates(
              scheduleIds
            );
            this.hideCancelled = false;
          } else if (action === "updated") {
            // TODO implement
          } else if (action === "added") {
            // TODO implement
          }
        };

        // Call the async function
        executeAction();
      }
    });
  },
  beforeDestroy() {
    // to avoid memory leaks
    EventBus.$off("reload-dialog");
  },
  methods: {
    undoCancellation(scheduleId, scheduleDate) {
      undoCancelOccurrence(scheduleId, scheduleDate)
        .then(async () => {
          const scheduleIds = this.schedules.map((s) => s.id);
          this.scheduleExclusionDates = await getScheduleExclusionDates(
            scheduleIds
          );
        })
        .catch((error) => {
          this.addErrorMessage(
            error.data.message || "Error restoring canceled schedule"
          );
        });
    },
    editSchedule(date, schedule) {
      const startTime = `${date} ${moment(schedule.startTime).format("HH:mm")}`;
      const endTime = `${date} ${moment(schedule.endTime).format("HH:mm")}`;

      const assignment = this.scheduleId2Assignment
        ? this.scheduleId2Assignment[schedule.id]
        : null;

      const teacher =
        assignment && assignment.teacherId
          ? this.teachers.find((t) => t.id == assignment.teacherId)
          : null;
      const studentSchedule = {
        id: schedule.id,
        teacher: teacher,
        teacherId: assignment ? assignment.teacherId : null,
        classroomId: assignment ? assignment.classroomId : null,
        studentFirstName: this.student.firstName,
        studentLastName: this.student.lastName,
        studentId: schedule.studentId,
        startTime,
        endTime,
        partOfRecurring: schedule.recurring,
        recurringDays: schedule.recurringDays,
        recurringStartDate: schedule.recurring ? schedule.startDate : null,
        recurringEndDate: schedule.recurringEndDate,
        programId: schedule.programId,
        centerId: schedule.centerId,
        notes: schedule.notes,
      };

      openEditStudentScheduleInstance({
        studentSchedule,
        teacher,
        parentID: this.uuID,
      });
    },
    fetchSchedule(student) {
      this.loaded = false;
      if (student == null) {
        return;
      }

      const studentIds = [student.id];
      const activeOnly = false; // get all student schedules
      const centerId = this.globalCenter.id;
      fetchStudentSchedulesByStudentIds(studentIds, centerId, activeOnly).then(
        async (data) => {
          this.schedules = data.map((d) => {
            // Create moment objects for start date and time
            const startDate = moment(d.startDate);
            const startTime = moment(d.startTime, "HH:mm:ss");

            // Create start date-time by setting hours, minutes, and seconds of start date to start time
            startDate.hours(startTime.hours());
            startDate.minutes(startTime.minutes());
            startDate.seconds(startTime.seconds());

            // Create end date-time. If end date is null, copy the start date
            const endDate = d.recurring
              ? moment(d.recurringEndDate)
              : startDate.clone();
            const endTime = moment(d.endTime, "HH:mm:ss");

            // Set hours, minutes, and seconds of end date to end time
            endDate.hours(endTime.hours());
            endDate.minutes(endTime.minutes());
            endDate.seconds(endTime.seconds());

            return {
              id: d.id,
              centerId: d.centerId,
              studentFirstName: student.firstName,
              studentLastName: student.lastName,
              startDate: startDate,
              startTime: startTime,
              endDate: endDate,
              endTime: endTime,
              teacherId: d.teacherId,
              classroomId: d.classroomId,
              studentId: d.studentId,
              recurring: d.recurring,
              recurringDays: d.recurringDays,
              recurringEndDate: d.recurringEndDate,
              programId: d.programId,
              createdOn: d.createdTime,
            };
          });

          // fetch schedule teacher/classroom assignments
          const scheduleIds = this.schedules.map((s) => s.id);
          this.scheduleId2Assignment = await getScheduleAssignments(
            scheduleIds
          );
          this.scheduleExclusionDates = await getScheduleExclusionDates(
            scheduleIds
          );

          this.loaded = true;
        }
      );
    },
    loadPrograms() {
      this.$axios.get("/program/get-by-center").then((resp) => {
        this.programId2Name = resp.data.reduce((acc, p) => {
          acc[p.id] = p.name;
          return acc;
        }, {});
      });
    },
    getProgramName(id) {
      return this.programId2Name[id];
    },
    getAssignmentInfo(schedule) {
      const assignment = this.scheduleId2Assignment
        ? this.scheduleId2Assignment[schedule.id]
        : null;
      if (assignment) {
        if (assignment.type == "teacher") {
          return `With ${assignment.teacherFirstName} ${assignment.teacherLastName}`;
        }
        if (assignment.type == "classroom") {
          return `Classroom: ${assignment.classroomName}`;
        }
      } else {
        return null;
      }
    },
    viewAttendances() {
      openStudentAttendance({ student: this.student, programIds: null });
    },
    newStudentSchedule() {
      openNewStudentSchedule({ student: this.student });
    },
    fetchDocumentsCounts() {
      const params = {
        student_ids: this.student.id,
      };
      this.$axios
        .get("/studentRequirementSubmission/submission-count-by-student", {
          params,
        })
        .then((resp) => {
          const studentId2Count = resp.data;
          const count = studentId2Count[this.student.id];
          this.studentDocumentCount = count ? count : 0;
        });
    },
    openCompliance() {
      const complianceUrl = process.env.VUE_APP_COMPLIANCE_URL;
      const moduleUrl = `${complianceUrl}/#/student/view/${this.student.id}`;
      window.location.href = moduleUrl;
    },
  },
};
</script>

<style>
.v-slide-group__prev {
  display: none !important;
}
</style>